/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useDeviceInfo } from '@airasia-rn/ui';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { pd } from '@airasia-phoenix/react-native/tokens';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { formatDeletionDate } from '../../utils/accountRecovery';
import { styles } from './styles';

const WEB_DIALOG_BUTTON_WIDTH = 134;

export interface KeepAccountPopupViewProps {
  visible: boolean;
  deleteScheduledAt?: string;
  onNo: () => void;
  onYesLogIn: () => void;
  onClose?: () => void;
}

/**
 * Keep-account confirmation during sign-in (USER_SCHEDULED_FOR_DELETION).
 * Custom Modal card (HotelDialogView pattern) — Phoenix DialogView overflows in
 * Storybook/web because it sizes off screen width via FullWindowOverlay.
 */
export const KeepAccountPopupView: React.FC<KeepAccountPopupViewProps> = ({
  visible,
  deleteScheduledAt,
  onNo,
  onYesLogIn,
  onClose,
}) => {
  const { t, i18n } = useTranslation('account');
  const { isWeb, isMobileWidth } = useDeviceInfo();
  const isWebSheet = isWeb && isMobileWidth;
  const isWebDialog = isWeb && !isMobileWidth;

  const dateLabel = useMemo(
    () => formatDeletionDate(deleteScheduledAt, i18n.language),
    [deleteScheduledAt, i18n.language],
  );

  const descriptionParts = useMemo(() => {
    // No usable deadline from the API: say nothing rather than guess a date.
    if (!dateLabel) {
      return {
        before: t('keepAccount.descriptionNoDate'),
        date: '',
        after: '',
      };
    }
    const full = t('keepAccount.description', { date: dateLabel });
    const index = full.indexOf(dateLabel);
    if (index < 0) {
      return { before: full, date: '', after: '' };
    }
    return {
      before: full.slice(0, index),
      date: dateLabel,
      after: full.slice(index + dateLabel.length),
    };
  }, [dateLabel, t]);

  const handleClose = onClose ?? onNo;

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View
        style={[styles.backdrop, isWebSheet && styles.backdropWebSheet]}
        testID="keep-account-backdrop"
      >
        <Pressable
          style={styles.backdropPressable}
          onPress={handleClose}
          accessibilityRole="button"
          testID="keep-account-backdrop-pressable"
        />
        <View
          style={[
            styles.card,
            isWebDialog && styles.cardWebDialog,
            isWebSheet && styles.cardWebSheet,
          ]}
          testID="keep-account-card"
        >
          <Text
            style={[
              getTextStyle(
                isWebDialog
                  ? pd.sys.typescale.body_text_medium
                  : pd.sys.typescale.heading_3,
                pd.sys.colour.content_primary,
              ),
              styles.title,
              isWebDialog && styles.textWebDialog,
            ]}
            testID="keep-account-title"
          >
            {t('keepAccount.title')}
          </Text>

          <Text
            style={[
              getTextStyle(
                pd.sys.typescale.sub_body_text,
                pd.sys.colour.content_secondary,
              ),
              styles.body,
              isWebDialog && styles.textWebDialog,
            ]}
            testID="keep-account-description"
          >
            {descriptionParts.before}
            {descriptionParts.date ? (
              <Text
                style={getTextStyle(
                  pd.sys.typescale.sub_body_text_medium,
                  pd.sys.colour.content_primary,
                )}
              >
                {descriptionParts.date}
              </Text>
            ) : null}
            {descriptionParts.after}
          </Text>

          <View
            style={[styles.actions, isWebDialog && styles.actionsWebDialog]}
          >
            <ButtonView
              type={ButtonViewType.SECONDARY_REGULAR}
              text={t('keepAccount.noButton')}
              onPress={onNo}
              testID="keep-account-no"
              width={isWebDialog ? WEB_DIALOG_BUTTON_WIDTH : undefined}
            />
            <ButtonView
              type={ButtonViewType.PRIMARY_REGULAR}
              text={t('keepAccount.yesButton')}
              onPress={onYesLogIn}
              testID="keep-account-yes"
              width={isWebDialog ? WEB_DIALOG_BUTTON_WIDTH : undefined}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

KeepAccountPopupView.displayName = 'KeepAccountPopupView';
